import {
  Accordion, AccordionDetails, AccordionSummary, Alert, CircularProgress,
  Pagination, Stack, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { HistoryChanges } from "features/histories/HistoryChanges";
import I18n from "i18n";
import { useEffect, useState } from "react";
import { accessControlError, AccessControlHistory, loadAccessControlHistory } from "./accessControlApi";

export default function AccessControlHistoryList() {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [history, setHistory] = useState<AccessControlHistory>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError("");
    loadAccessControlHistory(page).then(result => {
      if (!active) return;
      if (result.success && result.data) setHistory(result.data);
      else setError(accessControlError(result.error, I18n.t("AccessControl.historyError")));
    }).catch((reason: unknown) => {
      if (active) setError(reason instanceof Error ? reason.message : String(reason));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, page]);

  return (
    <Accordion expanded={open} onChange={(_, expanded) => setOpen(expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>{I18n.t("History.title")}</AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {loading && <CircularProgress size={24} />}
          {error && <Alert severity="error">{error}</Alert>}
          {!loading && !error && history?.data.map(entry => (
            <Stack key={entry.id} spacing={1}>
              <Typography fontWeight="bold">
                {entry.created_at} — {entry.user_name ?? I18n.t("AccessControl.unknownUser")} (#{entry.user_id})
              </Typography>
              {entry.class_name === "AccessControlEntry" ? (
                <Stack>
                  <Typography>
                    <strong>{I18n.t(`AccessControl.historyEvents.${entry.event}`)}{
                      entry.data.find(change => change.id === "type") && <> · {
                      I18n.t(`AccessControl.entryTypes.${String(
                        entry.data.find(change => change.id === "type")?.new
                        ?? entry.data.find(change => change.id === "type")?.old,
                      )}`)}</>
                    }: </strong>
                    {entry.item_label}
                  </Typography>
                  {entry.data.filter(change => change.id === "expires_at").map(change => (
                    <Typography key={change.id} variant="body2">
                      {I18n.t("AccessControl.expiresAt")}: {entry.event === "updated" && <>
                        {change.old ? new Date(String(change.old)).toLocaleString(I18n.locale)
                          : I18n.t("AccessControl.unlimited")} → </>}
                      {(entry.event === "deleted" ? change.old : change.new)
                        ? new Date(String(entry.event === "deleted" ? change.old : change.new)).toLocaleString(I18n.locale)
                        : I18n.t("AccessControl.unlimited")}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <HistoryChanges objectName="AccessControlSettings" value={entry.data} event={entry.event} />
              )}
            </Stack>
          ))}
          {!loading && !error && history?.total === 0 && <Typography>{I18n.t("AccessControl.noHistory")}</Typography>}
          {history && history.last_page > 1 &&
            <Pagination count={history.last_page} page={page} disabled={loading} onChange={(_, value) => setPage(value)} />}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
